/**
 * Textos comerciales de cada función gateada por plan (spec 007).
 *
 * FUENTE ÚNICA en el frontend para: el nombre de la función, la pantalla de
 * bloqueo (PlanGate/LockedFeatureCard), el aviso global PlanFeatureLocked y
 * los beneficios que se listan al sugerir un cambio de plan.
 *
 * El plan requerido DEBE coincidir con FEATURE_GATES del backend
 * (apps/backend/src/common/plan-features.ts): 3000 = Estándar, 4000 = Pro.
 *
 * Reglas de redacción: español neutro, para salones/spas/barberías, sin
 * emojis, sin prometer nada que el sistema no haga hoy.
 */

export type RequiredPlan = "Emprendedor" | "Estándar" | "Pro";

export type FeatureIconKey =
  | "compras"
  | "marketing"
  | "analitica"
  | "pedidos"
  | "cobrar"
  | "cambio"
  | "comisiones"
  | "integraciones"
  | "reportes"
  | "sedes"
  | "whatsapp"
  | "comandas";

export interface FeatureCopy {
  /** Nombre corto de la función, tal como aparece en el menú. */
  name: string;
  /** Titular orientado al beneficio. */
  title: string;
  /** Una línea que aterriza el titular. */
  subtitle: string;
  /** Exactamente 3 beneficios concretos. */
  bullets: [string, string, string];
  requiredPlan: RequiredPlan;
  icon: FeatureIconKey;
}

const COMPRAS: FeatureCopy = {
  name: "Compras y proveedores",
  title: "Compra mejor y controla lo que entra a tu almacén",
  subtitle: "Proveedores, órdenes de compra y facturas de compra en un solo lugar.",
  bullets: [
    "Registra a tus proveedores y emite órdenes de compra en PDF listas para enviar.",
    "Carga las facturas del proveedor (también desde el XML) y el stock ingresa solo a tu almacén.",
    "Lleva tus cuentas por pagar, transferencias entre almacenes, inventario físico e historial de movimientos.",
  ],
  requiredPlan: "Estándar",
  icon: "compras",
};

const MARKETING: FeatureCopy = {
  name: "Promociones y gift cards",
  title: "Haz que tus clientes vuelvan y gasten más en cada visita",
  subtitle: "Promociones, paquetes y gift cards que se cobran desde tu caja.",
  bullets: [
    "Crea promociones por servicio o producto, con descuento en porcentaje o monto fijo y fechas de vigencia.",
    "Arma paquetes de servicios con su propio precio para vender más en cada cita.",
    "Vende gift cards y cóbralas directo en la caja cuando tu cliente las use.",
  ],
  requiredPlan: "Estándar",
  icon: "marketing",
};

export const FEATURE_COPY: Record<string, FeatureCopy> = {
  compras: COMPRAS,
  // Alias legacy de 'compras' (contrato pre-007).
  inventarios: COMPRAS,
  marketing: MARKETING,
  giftcards: { ...MARKETING, name: "Gift cards" },
  analitica: {
    name: "Explorador de analítica",
    title: "Responde tus propias preguntas sobre el negocio",
    subtitle: "Arma reportes a tu medida, sin esperar a que alguien te los prepare.",
    bullets: [
      "Cruza ventas, clientes, servicios, productos y reservas eligiendo filas, valores y filtros.",
      "Descubre qué servicios, colaboradores o días te dejan más, por sede y por periodo.",
      "Guarda tus análisis favoritos y expórtalos a Excel con un clic.",
    ],
    requiredPlan: "Estándar",
    icon: "analitica",
  },
  pedidos_internos: {
    name: "Pedidos internos",
    title: "Abastece a tus sedes desde la central con orden",
    subtitle: "Pedidos entre la central y tus sedes, con guía y seguimiento.",
    bullets: [
      "Recibe los pedidos de cada sede en una sola bandeja y valida qué se despacha.",
      "Genera la nota de venta interna y la guía de despacho en PDF.",
      "Sigue cada pedido hasta que la sede confirma la recepción, con devoluciones incluidas.",
    ],
    requiredPlan: "Estándar",
    icon: "pedidos",
  },
  cuentas_por_cobrar: {
    name: "Cuentas por cobrar",
    title: "Sabe siempre quién te debe y cuánto",
    subtitle: "Controla las ventas al crédito de tus clientes.",
    bullets: [
      "Ve todas las deudas pendientes en una lista, filtradas por cliente, estado y fecha.",
      "Registra abonos parciales o el pago total y el saldo se actualiza al momento.",
      "Consulta el historial de pagos de cada cuenta para cobrar sin discusiones.",
    ],
    requiredPlan: "Estándar",
    icon: "cobrar",
  },
  tipo_cambio: {
    name: "Tipo de cambio",
    title: "Trabaja en dólares con el tipo de cambio del día",
    subtitle: "Consulta y registra el tipo de cambio USD / PEN.",
    bullets: [
      "Consulta el tipo de cambio de compra y venta de cada día; si falta, se toma de SUNAT.",
      "Revisa el histórico por mes y año.",
      "Registra o corrige un tipo de cambio manual cuando lo necesites.",
    ],
    requiredPlan: "Estándar",
    icon: "cambio",
  },
  comisiones: {
    name: "Comisiones",
    title: "Paga comisiones justas sin hacer cuentas a mano",
    subtitle: "Define cómo gana cada colaborador y liquida en minutos.",
    bullets: [
      "Configura comisiones por puesto y categoría de servicio: porcentaje, monto fijo o escalas por producción.",
      "Liquida por rango de fechas y el sistema calcula cuánto le toca a cada colaborador.",
      "Registra los pagos y exporta el detalle a Excel para tu planilla.",
    ],
    requiredPlan: "Estándar",
    icon: "comisiones",
  },
  integraciones: {
    name: "Integraciones",
    title: "Conecta tu web o tienda online con tu salón",
    subtitle: "Una API para que tus otros sistemas trabajen con Prikly.",
    bullets: [
      "Genera API keys por sede y caja para cada integración.",
      "Tu web puede consultar tu catálogo de productos, servicios y gift cards.",
      "Las ventas de tu tienda online entran a Prikly y se registran en la caja que elijas.",
    ],
    requiredPlan: "Estándar",
    icon: "integraciones",
  },
  whatsapp_recordatorios: {
    name: "WhatsApp",
    title: "Reduce las citas perdidas",
    subtitle: "Recordatorios, confirmaciones y comprobantes por WhatsApp.",
    bullets: [
      "Recordatorios y confirmaciones de citas",
      "Comprobantes de venta por WhatsApp",
      "Usa tu propio número",
    ],
    requiredPlan: "Estándar",
    icon: "whatsapp",
  },
  // Niveles de reportes (decisión comercial 2026-09). Los reportes básicos no
  // se gatean; el mapa reporte → nivel vive en
  // modules/reports/models/consts/reportPlanTiers.ts.
  reportes_intermedios: {
    name: "Reportes intermedios",
    title: "Mide el rendimiento de tu equipo, tu almacén y tus compras",
    subtitle: "Reportes para decidir con números, no a ojo.",
    bullets: [
      "Productividad de cada estilista: ranking, comisiones, rentabilidad y detalle por día.",
      "Almacén bajo control: movimientos, kardex valorizado y cuánto vale tu inventario al cierre.",
      "Compras por proveedor, diferencias entre órdenes y facturas, venta de paquetes y notas de crédito por saldar.",
    ],
    requiredPlan: "Estándar",
    icon: "reportes",
  },
  reportes_avanzados: {
    name: "Reportes avanzados y exportación contable",
    title: "Entrega a tu contador todo listo, sin digitar nada",
    subtitle: "Reportes tributarios y exportación contable en el formato que se necesita.",
    bullets: [
      "Kardex SUNAT (Formato 13.1) listo para el PLE.",
      "Cruza tu registro de SUNAT contra tus ventas y detecta los comprobantes que no cuadran.",
      "Exporta la provisión de ventas y compras para CONCAR y los registros de ventas y compras del PLE.",
    ],
    requiredPlan: "Pro",
    icon: "reportes",
  },
  // 'autoreportes' (Pro) no tiene copy propio a propósito: la función aún no
  // existe en el producto y no se promociona. Cae en el copy genérico.
  multilocal: {
    name: "Multi-sede",
    title: "Administra todas tus sedes desde una sola cuenta",
    subtitle: "Abre nuevas sedes y gestiónalas desde la central.",
    bullets: [
      "Crea nuevas sedes y cambia entre ellas desde el menú.",
      "Comparte desde la sede central tu catálogo de servicios, productos y clientes.",
      "Compara ventas y operación de cada sede con reportes por sede.",
    ],
    requiredPlan: "Pro",
    icon: "sedes",
  },
  comandas: {
    name: "Comandas",
    title: "Toma pedidos desde la tablet",
    subtitle: "Comandas incluidas en todos los planes.",
    bullets: [
      "Tus colaboradores cargan los servicios desde una tablet.",
      "La caja recibe la comanda lista para cobrar.",
      "Menos errores al cobrar.",
    ],
    requiredPlan: "Emprendedor",
    icon: "comandas",
  },
};

/** Copy de una feature; si no existe, uno genérico que no promete nada. */
const FALLBACK_META: Record<string, { name: string; requiredPlan: RequiredPlan }> = {
  autoreportes: { name: "Reportes automáticos", requiredPlan: "Pro" },
};

export const getFeatureCopy = (feature: string): FeatureCopy =>
  FEATURE_COPY[feature] ?? {
    name: FALLBACK_META[feature]?.name ?? "Esta función",
    title: "Esta función no está incluida en tu plan",
    subtitle: "Mejora tu plan para empezar a usarla.",
    bullets: [
      "Accede a más herramientas para tu negocio.",
      "Sin perder nada de lo que ya tienes configurado.",
      "El cambio se aplica a tu cuenta al confirmar el pago.",
    ],
    requiredPlan: FALLBACK_META[feature]?.requiredPlan ?? "Estándar",
    icon: "reportes",
  };

/** Clave del catálogo de precios (plansNet) para un nombre comercial de plan. */
export const planKeyOf = (plan: string): string =>
  String(plan ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/^plan\s+/, "")
    .trim();

/** Nombre comercial del plan según jerarquía (Emprendedor 2000, Estándar 3000, Pro 4000). */
export const planNameForHierarchy = (hierarchy: number): RequiredPlan | null => {
  if (hierarchy >= 4000) return "Pro";
  if (hierarchy >= 3000) return "Estándar";
  if (hierarchy >= 2000) return "Emprendedor";
  return null;
};

/**
 * Funciones (con copy) que se ganan al pasar de un plan a otro. Sirve para el
 * modal de límite: "Pasar a Estándar incluye además: ...".
 */
export const featuresGainedTo = (target: RequiredPlan): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const [key, copy] of Object.entries(FEATURE_COPY)) {
    if (key === "inventarios" || key === "giftcards" || key === "whatsapp_recordatorios") continue;
    if (copy.requiredPlan !== target || seen.has(copy.name)) continue;
    seen.add(copy.name);
    out.push(copy.name);
  }
  return out;
};
